import "./observability.css";

import { GitBranch, Globe, Radar, Server, ShieldAlert } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";

import { readEnvName } from "@/lib/env";

import type { ObservabilityScope, ObservabilityService } from "./observability-api";
import { useBrowserFailures } from "./use-browser-failures";
import { useIncidentSurface } from "./use-incident-surface";
import { useRecentFailures } from "./use-recent-failures";

type DomainCard = {
  readonly name: string;
  readonly state: "err" | "warn" | "ok";
  readonly delta: string;
  readonly value: string;
  readonly unit: string;
  readonly footLeft: string;
  readonly footRight: string;
  readonly deploy?: string;
};

const BASE_DOMAIN_CARDS: ReadonlyArray<DomainCard> = [
  {
    name: "Backend",
    state: "err",
    delta: "▲ 12×",
    value: "0.31",
    unit: "% err",
    footLeft: "5xx · 3 services",
    footRight: "p95 142ms",
  },
  {
    name: "Browser",
    state: "warn",
    delta: "▲ 3×",
    value: "59",
    unit: "JS err/min",
    footLeft: "12 sessions affected",
    footRight: "2 routes",
  },
  {
    name: "Infrastructure",
    state: "ok",
    delta: "flat",
    value: "100",
    unit: "% healthy",
    footLeft: "24/24 workers ready",
    footRight: "cpu 41%",
  },
];

const WINDOW_OPTIONS = [15, 60, 180] as const;
const OBSERVABILITY_SCOPE_OPTIONS: ReadonlyArray<{
  value: ObservabilityScope;
  label: string;
}> = [
  { value: "identity", label: "Identity path" },
  { value: "all", label: "All services" },
];
const IDENTITY_SERVICE_OPTIONS: ReadonlyArray<{
  value: ObservabilityService | "all";
  label: string;
}> = [
  { value: "identity-api", label: "Identity API" },
  { value: "cognito-post-auth", label: "Post-auth" },
  { value: "cognito-pre-token", label: "Pre-token" },
  { value: "all", label: "All identity" },
];
const OBSERVABILITY_SERVICE_OPTIONS = new Set<ObservabilityService | "all">(
  IDENTITY_SERVICE_OPTIONS.map((option) => option.value)
);

type ObservabilityHandoff = {
  readonly source: "browser" | "api";
  readonly targetEnvironment: "staging" | "prod" | null;
  readonly route: string | null;
  readonly title: string | null;
  readonly action: string | null;
  readonly statusCode: number | null;
  readonly requestId: string | null;
  readonly requestPath: string | null;
};

function formatMinutesLabel(minutes: number): string {
  if (minutes < 60) return `${minutes}m`;
  if (minutes % 60 === 0) return `${minutes / 60}h`;
  return `${minutes}m`;
}

function sanitizeSearchParam(value: string | null, maxLength: number): string | null {
  if (!value) return null;

  const normalized = value.replace(/\s+/g, " ").trim().slice(0, maxLength);
  return normalized.length > 0 ? normalized : null;
}

function parseScopeParam(value: string | null): ObservabilityScope {
  return value === "all" ? "all" : "identity";
}

function parseServiceParam(value: string | null): ObservabilityService | "all" {
  if (value && OBSERVABILITY_SERVICE_OPTIONS.has(value as ObservabilityService | "all")) {
    return value as ObservabilityService | "all";
  }

  return "identity-api";
}

function parseTargetEnvironmentParam(value: string | null): "staging" | "prod" | null {
  if (value === "staging" || value === "prod") return value;
  return null;
}

function parseFailureSourceParam(value: string | null): "browser" | "api" {
  return value === "browser" ? "browser" : "api";
}

function parseStatusCodeParam(value: string | null): number | null {
  if (!value) return null;

  const parsed = Number(value);
  return Number.isInteger(parsed) && parsed >= 0 ? parsed : null;
}

function readHandoff(searchParams: URLSearchParams): ObservabilityHandoff | null {
  if (searchParams.get("handoff") !== "core-org-management") return null;

  return {
    source: parseFailureSourceParam(searchParams.get("source")),
    targetEnvironment: parseTargetEnvironmentParam(searchParams.get("target")),
    route: sanitizeSearchParam(searchParams.get("route"), 120),
    title: sanitizeSearchParam(searchParams.get("title"), 120),
    action: sanitizeSearchParam(searchParams.get("action"), 120),
    statusCode: parseStatusCodeParam(searchParams.get("statusCode")),
    requestId: sanitizeSearchParam(searchParams.get("requestId"), 120),
    requestPath: sanitizeSearchParam(searchParams.get("requestPath"), 160),
  };
}

function formatTenantLabel(environment: string): string {
  return `tenant endure-${environment}`;
}

function formatSeverityLabel(severity: "sev1" | "sev2" | "sev3"): string {
  return severity.toUpperCase();
}

function formatChangeToken(value: string | null, fallback: string): string {
  return value ?? fallback;
}

function formatCommitToken(value: string | null, fallback: string): string {
  return value ? value.slice(0, 7) : fallback;
}

function formatPullRequestToken(value: string | null, hasDeployContext: boolean): string {
  return value ?? (hasDeployContext ? "No linked PR" : "PR pending");
}

function isTargetEnvironment(environment: string): boolean {
  return environment === "staging" || environment === "prod";
}

function formatScopeLabel(scope: ObservabilityScope): string {
  return scope === "identity" ? "Identity critical path" : "All services";
}

function formatScopeSummary(scope: ObservabilityScope): string {
  return scope === "identity"
    ? "identity-api plus Cognito auth triggers for org-management and sign-in regressions."
    : "all allowlisted backend services in this environment.";
}

function formatServiceFocusSummary(service: ObservabilityService | "all"): string {
  switch (service) {
    case "identity-api":
      return "Identity API is selected by default because org-management work is landing there first.";
    case "cognito-post-auth":
      return "Focused on the post-auth trigger for sign-in and session handoff regressions.";
    case "cognito-pre-token":
      return "Focused on the pre-token trigger for claims and token-shaping regressions.";
    default:
      return "All identity-path services are grouped together for auth-wide incidents.";
  }
}

function formatServiceChipLabel(service: ObservabilityService | "all"): string {
  const option = IDENTITY_SERVICE_OPTIONS.find((item) => item.value === service);
  return option?.label ?? service;
}

function formatHandoffSource(source: "browser" | "api"): string {
  return source === "browser" ? "Local browser failure" : "Local API failure";
}

function severityTone(severity: "sev1" | "sev2" | "sev3"): "err" | "warn" | "ok" {
  if (severity === "sev1") return "err";
  if (severity === "sev2") return "warn";
  return "ok";
}

function formatRelative(iso: string): string {
  const diffMs = Math.max(0, Date.now() - new Date(iso).getTime());
  if (diffMs < 60_000) return `${Math.round(diffMs / 1000)}s ago`;
  if (diffMs < 3_600_000) return `${Math.round(diffMs / 60_000)}m ago`;
  if (diffMs < 86_400_000) return `${Math.round(diffMs / 3_600_000)}h ago`;
  return `${Math.round(diffMs / 86_400_000)}d ago`;
}

function toStackTrace(message: string, title: string): string {
  if (message.includes("\n")) return message;
  return [title, `  ${message}`].join("\n");
}

function DomainRibbonCard(card: DomainCard): React.ReactElement {
  return (
    <article className="obs-ribbon-card">
      <div className="obs-ribbon-card__top">
        <span className="obs-ribbon-card__name">
          <span className={`obs-ribbon-card__state ${card.state}`} aria-hidden="true" />
          {card.name}
        </span>
        <span className="obs-ribbon-card__delta">{card.delta}</span>
      </div>
      {card.deploy ? (
        <div className="obs-ribbon-card__change">
          <div className="obs-ribbon-card__deploy">{card.value}</div>
          <div className="obs-ribbon-card__unit">{card.unit}</div>
        </div>
      ) : (
        <div className="obs-ribbon-card__metric">
          <span className="obs-ribbon-card__value">{card.value}</span>
          <span className="obs-ribbon-card__unit">{card.unit}</span>
        </div>
      )}
      <div className="obs-ribbon-card__foot">
        <span>{card.footLeft}</span>
        <span>{card.footRight}</span>
      </div>
    </article>
  );
}

function FailureRow({
  failure,
  isOpen,
  onToggle,
}: {
  failure: {
    readonly severity: "err";
    readonly label: string;
    readonly title: string;
    readonly fingerprint: string;
    readonly service: string;
    readonly environment: string;
    readonly firstSeen: string;
    readonly lastSeen: string;
    readonly events: string;
    readonly window: string;
    readonly change: string;
    readonly stack: string;
    readonly traceId: string | null;
    readonly logGroup: string;
  };
  isOpen: boolean;
  onToggle: () => void;
}): React.ReactElement {
  return (
    <div className="obs-panel">
      <button
        type="button"
        className={`obs-failure-row${isOpen ? " is-open" : ""}`}
        onClick={onToggle}
        aria-expanded={isOpen}
      >
        <span className={`obs-failure-row__bar ${failure.severity}`} aria-hidden="true" />
        <div className="obs-failure-row__main">
          <div className="obs-failure-row__titleline">
            <span className={`obs-badge ${failure.severity}`}>{failure.label}</span>
            <span className="obs-failure-row__title">{failure.title}</span>
            <span className="obs-failure-row__fingerprint">{failure.fingerprint}</span>
          </div>
          <div className="obs-failure-row__meta">
            <span>{failure.service}</span>
            <span>·</span>
            <span>{failure.environment}</span>
            <span>·</span>
            <span>first {failure.firstSeen}</span>
            <span>·</span>
            <span>last {failure.lastSeen}</span>
          </div>
          <div className="obs-failure-row__metrics">
            <span>trace {failure.traceId ?? "n/a"}</span>
            <span>·</span>
            <span>{failure.logGroup}</span>
          </div>
        </div>
        <div>
          <div className="obs-failure-row__count">{failure.events}</div>
          <div className="obs-failure-row__count-label">events · {failure.window}</div>
        </div>
        <div className="obs-failure-row__change">
          <span className="obs-deploy-chip">
            <GitBranch aria-hidden="true" />
            {failure.change}
          </span>
          <span className="obs-failure-row__count-label">tap for evidence</span>
        </div>
      </button>
      {isOpen ? (
        <div className="obs-failure-detail">
          <pre className="obs-stacktrace">{failure.stack}</pre>
          <ul className="obs-detail-list">
            <li>
              <span className="obs-detail-list__key">env</span>
              <span className="obs-detail-list__value">{failure.environment}</span>
            </li>
            <li>
              <span className="obs-detail-list__key">log group</span>
              <span className="obs-detail-list__value">{failure.logGroup}</span>
            </li>
            <li>
              <span className="obs-detail-list__key">first seen</span>
              <span className="obs-detail-list__value">{failure.firstSeen}</span>
            </li>
            <li>
              <span className="obs-detail-list__key">trace id</span>
              <span className="obs-detail-list__value">{failure.traceId ?? "n/a"}</span>
            </li>
          </ul>
        </div>
      ) : null}
    </div>
  );
}

function BrowserFailureRow({
  failure,
}: {
  failure: {
    readonly title: string;
    readonly source: "browser" | "api";
    readonly route: string;
    readonly targetEnvironment: string;
    readonly message: string;
    readonly lastSeen: string;
    readonly events: string;
    readonly action: string | null;
    readonly statusCode: number | null;
    readonly requestId: string | null;
    readonly requestPath: string | null;
  };
}): React.ReactElement {
  return (
    <div className="obs-browser-row">
      <div className="obs-browser-row__main">
        <div className="obs-browser-row__titleline">
          <span className={`obs-badge ${failure.source === "api" ? "warn" : "err"}`}>
            {failure.source === "api" ? "API" : "JS"}
          </span>
          <span className="obs-browser-row__title">{failure.title}</span>
        </div>
        <div className="obs-browser-row__meta">
          <span>{failure.route}</span>
          <span>·</span>
          <span>target {failure.targetEnvironment}</span>
          <span>·</span>
          <span>last {failure.lastSeen}</span>
        </div>
        <p className="obs-browser-row__message">{failure.message}</p>
        <div className="obs-browser-row__detail">
          {failure.action ? <span>{failure.action}</span> : null}
          {failure.statusCode !== null ? (
            <>
              <span>·</span>
              <span>status {failure.statusCode}</span>
            </>
          ) : null}
          {failure.requestId ? (
            <>
              <span>·</span>
              <span>request {failure.requestId}</span>
            </>
          ) : null}
          {failure.requestPath ? (
            <>
              <span>·</span>
              <code>{failure.requestPath}</code>
            </>
          ) : null}
        </div>
      </div>
      <div className="obs-browser-row__count">
        <div className="obs-failure-row__count">{failure.events}</div>
        <div className="obs-failure-row__count-label">local events</div>
      </div>
    </div>
  );
}

export function ObservabilityPage(): React.ReactElement {
  const [searchParams] = useSearchParams();
  const requestedScope = parseScopeParam(searchParams.get("scope"));
  const requestedService = parseServiceParam(searchParams.get("service"));
  const handoff = readHandoff(searchParams);
  const [scope, setScope] = useState<ObservabilityScope>(requestedScope);
  const [serviceFocus, setServiceFocus] = useState<ObservabilityService | "all">(
    requestedScope === "identity" ? requestedService : "all"
  );

  useEffect(() => {
    setScope(requestedScope);
    setServiceFocus(requestedScope === "identity" ? requestedService : "all");
  }, [requestedScope, requestedService]);

  const selectedService = scope === "identity" && serviceFocus !== "all" ? serviceFocus : undefined;
  const { data, isLoading, error, windowMinutes, setWindowMinutes, refresh } = useRecentFailures(
    scope,
    selectedService
  );
  const {
    data: incidentData,
    isLoading: incidentLoading,
    error: incidentError,
    refresh: refreshIncident,
  } = useIncidentSurface(windowMinutes, scope, selectedService);
  const {
    items: browserFailures,
    captureConfigured: localBrowserCaptureConfigured,
    clear: clearBrowserFailures,
  } = useBrowserFailures("organization");
  const [openFingerprint, setOpenFingerprint] = useState<string | null>(null);

  const normalizedFailures = useMemo(
    () =>
      (data?.items ?? []).map((item) => ({
        severity: "err" as const,
        label: "ERR",
        title: item.title,
        fingerprint: item.fingerprint,
        service: item.service,
        environment: item.environment,
        firstSeen: formatRelative(item.firstSeen),
        lastSeen: formatRelative(item.lastSeen),
        events: String(item.eventCount),
        window: formatMinutesLabel(data?.window.minutes ?? windowMinutes),
        change: item.kind,
        stack: toStackTrace(item.message, item.title),
        traceId: item.evidence.traceId,
        logGroup: item.evidence.logGroup,
      })),
    [data?.items, data?.window.minutes, windowMinutes]
  );

  useEffect(() => {
    const firstFailure = normalizedFailures[0];

    if (!firstFailure) {
      setOpenFingerprint(null);
      return;
    }

    setOpenFingerprint((current) => current ?? firstFailure.fingerprint);
  }, [normalizedFailures]);
  const currentEnv = incidentData?.environment ?? data?.environment ?? readEnvName();
  const targetEnvironment = isTargetEnvironment(currentEnv);
  // Prefer incident-surface's `browser_capture_configured` when loaded —
  // recent-failures may still be loading/errored and we don't want the
  // UI to misreport browser-capture status from the slower of the two
  // streams. Fall back to recent-failures when incident-surface is
  // absent.
  const browserCaptureConfigured =
    localBrowserCaptureConfigured ||
    incidentData?.browser_capture_configured ||
    data?.browser_capture_configured;
  const browserStatus = browserCaptureConfigured ? "Browser live" : "Browser pending";
  const normalizedBrowserFailures = useMemo(
    () =>
      browserFailures.map((item) => ({
        title: item.title,
        source: item.source,
        route: item.route,
        targetEnvironment: item.targetEnvironment,
        message: item.message,
        lastSeen: formatRelative(item.lastSeen),
        events: String(item.eventCount),
        action: item.action,
        statusCode: item.statusCode,
        requestId: item.requestId,
        requestPath: item.requestPath,
      })),
    [browserFailures]
  );
  const scopeLabel = formatScopeLabel(scope);
  const scopeSummary = formatScopeSummary(scope);
  const serviceFocusSummary = formatServiceFocusSummary(serviceFocus);
  const topbarFocusSummary =
    scope === "identity"
      ? serviceFocusSummary
      : "All allowlisted backend services are visible in this wider view.";
  const activeIncident = incidentData?.activeIncident ?? null;
  const hasDeployContext = Boolean(
    activeIncident?.changeContext.deployId ?? activeIncident?.changeContext.commitSha
  );
  const ribbonCards = useMemo<ReadonlyArray<DomainCard>>(() => {
    const incidentCard: DomainCard = activeIncident
      ? {
          name: "Last change",
          state: severityTone(activeIncident.severity),
          delta: formatRelative(activeIncident.changeContext.startedAt),
          value: formatChangeToken(activeIncident.changeContext.deployId, "Change pending"),
          unit: activeIncident.service,
          footLeft: `${formatPullRequestToken(activeIncident.changeContext.pullRequest, hasDeployContext)} · ${activeIncident.changeContext.author}`,
          footRight: "correlated",
          deploy: `${formatChangeToken(activeIncident.changeContext.deployId, "Change pending")} · ${activeIncident.service}`,
        }
      : {
          name: "Last change",
          state: "warn",
          delta: "waiting",
          value: "No active change",
          unit: currentEnv,
          footLeft: "incident surface",
          footRight: "standby",
          deploy: `No active change · ${currentEnv}`,
        };

    return [...BASE_DOMAIN_CARDS, incidentCard];
  }, [activeIncident, currentEnv, hasDeployContext]);

  const refreshAll = (): void => {
    refresh();
    refreshIncident();
  };

  return (
    <div className="obs-page" data-testid="observability-page">
      <section className="obs-topbar" aria-label="Observability controls">
        <div className="obs-topbar__main">
          <div className="obs-topbar__eyebrow">Ops Console / Incident Desk</div>
          <div className="obs-topbar__title-row">
            <h1 className="obs-topbar__title">Observability</h1>
            <span className="obs-topbar__flag">
              <ShieldAlert aria-hidden="true" size={14} />
              Internal only
            </span>
          </div>
          <p className="obs-topbar__summary">
            Current environment <strong>{currentEnv}</strong>. This slice is tuned for staging and
            prod identity/org-management regressions, with{" "}
            <strong>{scopeLabel.toLowerCase()}</strong> selected by default. {topbarFocusSummary}{" "}
            {browserStatus.toLowerCase()} for local-against-staging org-management work.
          </p>
          <div className="obs-topbar__status" aria-label="Observability access advisory">
            <span className="obs-topbar__status-item">Access logged</span>
            <span className="obs-topbar__status-item">PII auto-redacted in evidence</span>
            <span className="obs-topbar__status-item">{formatTenantLabel(currentEnv)}</span>
            <span className="obs-topbar__status-item">staging + prod focus</span>
            {!targetEnvironment ? (
              <span className="obs-topbar__status-item">preview only in {currentEnv}</span>
            ) : null}
          </div>
        </div>
        <div className="obs-topbar__actions">
          <button type="button" className="obs-chip-button is-live" onClick={refreshAll}>
            <span className="obs-chip-button__dot" aria-hidden="true" />
            Live
          </button>
          {WINDOW_OPTIONS.map((minutes) => (
            <button
              key={minutes}
              type="button"
              aria-pressed={windowMinutes === minutes}
              className={`obs-chip-button${windowMinutes === minutes ? " is-live" : ""}`}
              onClick={(): void => setWindowMinutes(minutes)}
            >
              {formatMinutesLabel(minutes)}
            </button>
          ))}
          {OBSERVABILITY_SCOPE_OPTIONS.map((option) => (
            <button
              key={option.value}
              type="button"
              aria-pressed={scope === option.value}
              className={`obs-chip-button${scope === option.value ? " is-live" : ""}`}
              onClick={(): void => {
                setScope(option.value);
                if (option.value !== "identity") {
                  setServiceFocus("all");
                  return;
                }
                setServiceFocus((current) => (current === "all" ? "identity-api" : current));
              }}
            >
              {option.label}
            </button>
          ))}
        </div>
      </section>

      {handoff ? (
        <section className="obs-handoff" data-testid="observability-handoff">
          <div className="obs-handoff__copy">
            <p className="obs-handoff__eyebrow">Core handoff active</p>
            <h2 className="obs-handoff__title">
              {formatHandoffSource(handoff.source)} opened from org-management work.
            </h2>
            <p className="obs-handoff__summary">
              Filters are preloaded to the identity critical path so you can move from the in-app
              failure tray into grouped staging/prod evidence without recreating the context by
              hand.
            </p>
          </div>
          <div className="obs-handoff__chips" aria-label="Core handoff details">
            <span className="obs-pill is-active">core</span>
            {handoff.targetEnvironment ? (
              <span className="obs-pill">{handoff.targetEnvironment}</span>
            ) : null}
            <span className="obs-pill">{formatServiceChipLabel(requestedService)}</span>
            {handoff.route ? <span className="obs-pill">{handoff.route}</span> : null}
            {handoff.action ? <span className="obs-pill">{handoff.action}</span> : null}
            {handoff.statusCode !== null ? (
              <span className="obs-pill">status {handoff.statusCode}</span>
            ) : null}
            {handoff.requestId ? (
              <span className="obs-pill">request {handoff.requestId}</span>
            ) : null}
            {handoff.requestPath ? (
              <span className="obs-pill">
                <code>{handoff.requestPath}</code>
              </span>
            ) : null}
            {handoff.title ? <span className="obs-pill">{handoff.title}</span> : null}
          </div>
        </section>
      ) : null}

      <section className="obs-ribbon" aria-label="Signal domains">
        {ribbonCards.map((card) => (
          <DomainRibbonCard key={card.name} {...card} />
        ))}
      </section>

      <section className="obs-grid">
        <div className="obs-panel">
          <div className="obs-panel__head">
            <div>
              <h2 className="obs-panel__title">Failure groups</h2>
              <p className="obs-panel__sub">
                Current environment: {currentEnv}. {scopeLabel} covers {scopeSummary} Live backend
                failures are grouped by service and error fingerprint, and local browser failures
                from org-management work show up alongside them in the browser lane.
              </p>
            </div>
            <div className="obs-panel__actions" aria-label="Failure filters">
              <span className="obs-pill is-active">
                <Radar aria-hidden="true" size={12} />
                Open
              </span>
              <span className="obs-pill">
                <Server aria-hidden="true" size={12} />
                Backend
              </span>
              <span className="obs-pill">
                <Globe aria-hidden="true" size={12} />
                {browserStatus}
              </span>
              <span className="obs-pill">
                <Radar aria-hidden="true" size={12} />
                {scopeLabel}
              </span>
              {scope === "identity" ? (
                <span className="obs-pill">
                  <Server aria-hidden="true" size={12} />
                  {formatServiceChipLabel(serviceFocus)}
                </span>
              ) : null}
            </div>
          </div>
          {scope === "identity" ? (
            <div className="obs-panel__head obs-panel__head--subfilter">
              <div>
                <h3 className="obs-panel__title">Auth component focus</h3>
                <p className="obs-panel__sub">{serviceFocusSummary}</p>
              </div>
              <div className="obs-panel__actions" aria-label="Auth component filters">
                {IDENTITY_SERVICE_OPTIONS.map((option) => (
                  <button
                    key={option.value}
                    type="button"
                    aria-pressed={serviceFocus === option.value}
                    className={`obs-chip-button${serviceFocus === option.value ? " is-live" : ""}`}
                    onClick={(): void => setServiceFocus(option.value)}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            </div>
          ) : null}
          <div className="obs-panel__body">
            {error ? (
              <div className="obs-status-card" data-testid="observability-error">
                <strong>Failed to load recent failures.</strong>
                <span>{error}</span>
                <button type="button" className="obs-chip-button" onClick={refresh}>
                  Retry
                </button>
              </div>
            ) : null}

            {isLoading ? (
              <div className="obs-status-card" data-testid="observability-loading">
                <strong>Loading recent backend failures…</strong>
                <span>Querying the current environment log groups.</span>
              </div>
            ) : null}

            {!isLoading && !error && normalizedFailures.length === 0 ? (
              <div className="obs-status-card" data-testid="observability-empty">
                <strong>No recent backend failures.</strong>
                <span>
                  Nothing matched the current {formatMinutesLabel(windowMinutes)} window for{" "}
                  {scopeLabel.toLowerCase()} in {currentEnv}.
                </span>
              </div>
            ) : null}

            {!isLoading && !error && normalizedFailures.length > 0 ? (
              <div className="obs-failure-list">
                {normalizedFailures.map((failure) => (
                  <FailureRow
                    key={failure.fingerprint}
                    failure={failure}
                    isOpen={openFingerprint === failure.fingerprint}
                    onToggle={(): void =>
                      setOpenFingerprint((current) =>
                        current === failure.fingerprint ? null : failure.fingerprint
                      )
                    }
                  />
                ))}
              </div>
            ) : null}
          </div>
        </div>

        <div className="obs-side">
          <div className="obs-panel">
            <div className="obs-panel__head">
              <div>
                <h2 className="obs-panel__title">Local browser failures</h2>
                <p className="obs-panel__sub">
                  Redacted browser and org-management API failures captured from this local browser
                  while you work against staging or prod.
                </p>
              </div>
              <div className="obs-panel__actions">
                <button type="button" className="obs-chip-button" onClick={clearBrowserFailures}>
                  Clear local signal
                </button>
              </div>
            </div>
            <div className="obs-panel__body">
              {normalizedBrowserFailures.length > 0 ? (
                <div className="obs-browser-list">
                  {normalizedBrowserFailures.map((failure) => (
                    <BrowserFailureRow
                      key={`${failure.source}:${failure.route}:${failure.title}:${failure.requestPath ?? "none"}`}
                      failure={failure}
                    />
                  ))}
                </div>
              ) : (
                <div className="obs-status-card" data-testid="observability-browser-empty">
                  <strong>Browser capture is live.</strong>
                  <span>
                    No local org-management browser failures have been recorded in this browser
                    session yet.
                  </span>
                </div>
              )}
            </div>
          </div>

          <div className="obs-panel">
            <div className="obs-panel__head">
              <div>
                <h2 className="obs-panel__title">Open alerts</h2>
                <p className="obs-panel__sub">
                  Alert-first view for the current {currentEnv} incident window and{" "}
                  {scopeLabel.toLowerCase()}.
                </p>
              </div>
            </div>
            <div className="obs-panel__body">
              {incidentError ? (
                <div className="obs-status-card" data-testid="incident-surface-error">
                  <strong>Failed to load the incident surface.</strong>
                  <span>{incidentError}</span>
                  <button type="button" className="obs-chip-button" onClick={refreshAll}>
                    Retry
                  </button>
                </div>
              ) : null}

              {incidentLoading ? (
                <div className="obs-status-card" data-testid="incident-surface-loading">
                  <strong>Loading alert timeline…</strong>
                  <span>Correlating alerts, evidence, and change context.</span>
                </div>
              ) : null}

              {!incidentLoading && !incidentError && (incidentData?.alerts.length ?? 0) > 0 ? (
                <ul className="obs-correlation-list">
                  {incidentData?.alerts.map((alert) => {
                    const tone = severityTone(alert.severity);
                    return (
                      <li key={alert.id}>
                        <span className="obs-correlation-list__service">
                          <span className={`obs-dot ${tone}`} aria-hidden="true" />
                          <span>
                            <strong>{formatSeverityLabel(alert.severity)}</strong> {alert.title}
                          </span>
                        </span>
                        <span
                          className={`obs-correlation-list__delta ${tone === "err" ? "up" : "flat"}`}
                        >
                          {alert.service}
                        </span>
                      </li>
                    );
                  })}
                </ul>
              ) : null}

              {!incidentLoading && !incidentError && (incidentData?.alerts.length ?? 0) === 0 ? (
                <div className="obs-status-card">
                  <strong>No open alerts.</strong>
                  <span>
                    The current {formatMinutesLabel(windowMinutes)} window has no grouped alerts for{" "}
                    {scopeLabel.toLowerCase()}.
                  </span>
                </div>
              ) : null}
            </div>
          </div>

          <div className="obs-panel">
            <div className="obs-panel__head">
              <div>
                <h2 className="obs-panel__title">Recent change</h2>
                <p className="obs-panel__sub">
                  Deploy, PR, and commit context next to the incident.
                </p>
              </div>
            </div>
            <div className="obs-panel__body">
              {activeIncident ? (
                <div className="obs-change-card">
                  <div className="obs-change-card__top">
                    <span className="obs-deploy-chip">
                      <GitBranch aria-hidden="true" />
                      {formatChangeToken(activeIncident.changeContext.deployId, "Change pending")}
                    </span>
                    <span className="obs-failure-row__count-label">
                      {formatRelative(activeIncident.changeContext.startedAt)}
                    </span>
                  </div>
                  <div className="obs-change-card__headline">{activeIncident.service}</div>
                  <div className="obs-change-card__meta">
                    <span>
                      {formatPullRequestToken(
                        activeIncident.changeContext.pullRequest,
                        hasDeployContext
                      )}
                    </span>
                    <span>·</span>
                    <span>
                      {formatCommitToken(
                        activeIncident.changeContext.commitSha,
                        hasDeployContext ? "No commit metadata" : "commit pending"
                      )}
                    </span>
                    <span>·</span>
                    <span>{activeIncident.changeContext.author}</span>
                  </div>
                  <p className="obs-panel__sub">{activeIncident.changeContext.summary}</p>
                </div>
              ) : (
                <div className="obs-status-card">
                  <strong>No correlated change yet.</strong>
                  <span>
                    Once an alert opens, the latest deploy and PR context will appear here.
                  </span>
                </div>
              )}
            </div>
          </div>

          <div className="obs-panel">
            <div className="obs-panel__body obs-agent">
              <div className="obs-agent__header">
                <span className="obs-agent__icon" aria-hidden="true">
                  <Radar size={16} />
                </span>
                <div>
                  <h3>Triage focus</h3>
                  <p>Human-first guidance for the active delivery path</p>
                </div>
              </div>
              <p className="obs-agent__body">What should I inspect next?</p>
              <div className="obs-agent__response">
                {activeIncident ? (
                  <>
                    Start with <strong>{activeIncident.service}</strong> and the grouped failure{" "}
                    <code>{activeIncident.title}</code>. The burst opened{" "}
                    {formatRelative(activeIncident.detectedAt)} in {currentEnv}, and the evidence
                    panel below is the fastest route into logs, traces, and supporting auth-path
                    signals.
                  </>
                ) : (
                  <>
                    No active incident is open right now. Keep the {scopeLabel.toLowerCase()} view
                    live while shipping identity or org-management changes in staging and prod.
                  </>
                )}
              </div>
              <div className="obs-agent__citations">
                <span className="obs-agent__citation">{scopeLabel}</span>
                <span className="obs-agent__citation">
                  {targetEnvironment ? currentEnv : `preview ${currentEnv}`}
                </span>
              </div>
              <div className="obs-agent__footer">
                <span>
                  Staging and prod only. Dev and integration are intentionally out of scope for this
                  slice.
                </span>
                <span>stay live</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="obs-panel">
        <div className="obs-panel__head">
          <div>
            <h2 className="obs-panel__title">Active incident timeline</h2>
            <p className="obs-panel__sub">
              Alert, evidence, and supporting signals in one workflow.
            </p>
          </div>
        </div>
        <div className="obs-panel__body">
          {incidentLoading ? (
            <div className="obs-status-card">
              <strong>Loading incident timeline…</strong>
              <span>Preparing evidence and change context.</span>
            </div>
          ) : null}

          {!incidentLoading && incidentError ? (
            <div className="obs-status-card">
              <strong>Incident timeline unavailable.</strong>
              <span>{incidentError}</span>
            </div>
          ) : null}

          {!incidentLoading && !incidentError && activeIncident ? (
            <div className="obs-incident-surface">
              <div className="obs-incident">
                <div className="obs-incident__summary">
                  <span className={`obs-badge ${severityTone(activeIncident.severity)}`}>
                    {formatSeverityLabel(activeIncident.severity)}
                  </span>
                  <div className="obs-incident__title">{activeIncident.title}</div>
                  <div className="obs-incident__meta">
                    owner {activeIncident.owner} · {activeIncident.affected} ·{" "}
                    {activeIncident.runbook}
                  </div>
                </div>
                <div className="obs-incident__timers">
                  <div className="obs-incident__timer">
                    <div className="obs-incident__timer-value">
                      {activeIncident.metrics.detectMinutes}m
                    </div>
                    <div className="obs-incident__timer-label">detect</div>
                  </div>
                  <div className="obs-incident__timer">
                    <div className="obs-incident__timer-value">
                      {activeIncident.metrics.openMinutes}m
                    </div>
                    <div className="obs-incident__timer-label">open</div>
                  </div>
                  <div className="obs-incident__timer">
                    <div className="obs-incident__timer-value">
                      {activeIncident.metrics.errorRatePct}%
                    </div>
                    <div className="obs-incident__timer-label">err</div>
                  </div>
                </div>
              </div>

              <div className="obs-incident-grid">
                <div className="obs-incident-column">
                  <h3 className="obs-panel__title">Evidence</h3>
                  <ul className="obs-incident-list">
                    {activeIncident.evidenceSources.map((source) => (
                      <li key={`${source.kind}:${source.locator}`}>
                        <strong>{source.label}</strong>
                        <span>{source.summary}</span>
                        <code>{source.locator}</code>
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="obs-incident-column">
                  <h3 className="obs-panel__title">Supporting signals</h3>
                  <ul className="obs-incident-list">
                    {activeIncident.supportingSignals.map((signal) => (
                      <li key={`${signal.kind}:${signal.label}`}>
                        <strong>{signal.label}</strong>
                        <span>{signal.summary}</span>
                        <code>
                          {signal.value} · {signal.state}
                        </code>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>

              <div className="obs-incident-column">
                <h3 className="obs-panel__title">Timeline</h3>
                <ul className="obs-timeline">
                  {activeIncident.timeline.map((entry) => (
                    <li key={`${entry.kind}:${entry.at}`}>
                      <span
                        className={`obs-dot ${entry.kind === "alert" || entry.kind === "backend" ? "err" : entry.kind === "deploy" || entry.kind === "browser" ? "warn" : "ok"}`}
                        aria-hidden="true"
                      />
                      <div className="obs-timeline__body">
                        <div className="obs-timeline__top">
                          <strong>{entry.title}</strong>
                          <span>{formatRelative(entry.at)}</span>
                        </div>
                        <span>{entry.summary}</span>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          ) : null}

          {!incidentLoading && !incidentError && !activeIncident ? (
            <div className="obs-status-card">
              <strong>No active incident timeline.</strong>
              <span>
                Once an alert opens in the current environment, evidence and change context will
                appear here.
              </span>
            </div>
          ) : null}
        </div>
      </section>
    </div>
  );
}
